import { useEffect, useState } from 'react';
import {
	ColumnDef,
	ColumnFiltersState,
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	SortingState,
	getSortedRowModel,
	getPaginationRowModel,
	useReactTable,
} from '@tanstack/react-table';

import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { ETablePagination } from '@/components/everridge/table/components/ETablePagination';
import { useTranslation } from 'react-i18next';

type ETableProps<TData, TValue> = {
	columns: ColumnDef<TData, TValue>[];
	data: TData[];
	search?: string;
	showDetails?: (id: string) => void;
	pagination?: boolean;
};

const ETable = <TData, TValue>({
	columns,
	data,
	search,
	showDetails,
	pagination = true,
}: ETableProps<TData, TValue>) => {
	const [sorting, setSorting] = useState<SortingState>([]);
	const [globalFilter, setGlobalFilter] = useState<ColumnFiltersState>([]);
	const { t } = useTranslation();

	const table = useReactTable({
		data,
		columns,
		getCoreRowModel: getCoreRowModel(),
		onSortingChange: setSorting,
		getSortedRowModel: getSortedRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		onGlobalFilterChange: setGlobalFilter,
		state: {
			sorting,
			globalFilter,
		},
	});

	useEffect(() => {
		if (search !== undefined) {
			table.setGlobalFilter(search);
		}
	}, [search, table]);

	return (
		<>
			<div className='mb-5 rounded-semi-xl border border-popover-100 bg-popover-10'>
				<Table>
					<TableHeader>
						{table.getHeaderGroups().map((headerGroup) => (
							<TableRow key={headerGroup.id}>
								{headerGroup.headers.map((header) => {
									return (
										<TableHead
											key={header.id}
											style={{
												maxWidth:
													header.column.columnDef
														.size,
											}}>
											{header.isPlaceholder
												? null
												: flexRender(
														header.column.columnDef
															.header,
														header.getContext(),
												  )}
										</TableHead>
									);
								})}
							</TableRow>
						))}
					</TableHeader>
					<TableBody>
						{table.getRowModel().rows?.length ? (
							table.getRowModel().rows.map((row) => (
								<TableRow
									key={row.id}
									data-state={
										row.getIsSelected() && 'selected'
									}
									className={'select-none cursor-pointer'}
									onClick={() =>
										showDetails?.(
											(row.original as { id: string }).id,
										)
									}>
									{row.getVisibleCells().map((cell) => (
										<TableCell
											key={cell.id}
											style={{
												maxWidth:
													cell.column.columnDef.size,
											}}>
											{flexRender(
												cell.column.columnDef.cell,
												cell.getContext(),
											)}
										</TableCell>
									))}
								</TableRow>
							))
						) : (
							<TableRow className={'select-none'}>
								<TableCell
									colSpan={columns.length}
									className='py-6 text-sm font-medium text-center'>
									{t('elements.table.body.empty')}
								</TableCell>
							</TableRow>
						)}
					</TableBody>
				</Table>
			</div>
			{pagination && data.length > 10 && (
				<ETablePagination table={table} />
			)}
		</>
	);
};

export default ETable;
